import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Banknote, TrendingUp, Users, Calendar, CheckCircle, Clock, AlertCircle, RefreshCw } from 'lucide-react';

// Use environment variable or fallback to localhost
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface FeeData {
  total: number;
  collected: number;
  pending: number;
  collectionRate: number;
}

interface ProviderBreakdown {
  providerId: string;
  providerName: string;
  providerEmail: string;
  providerAvatar?: string;
  completedBookings: number;
  totalRevenue: number;
  platformFee: number;
  gst: number;
  totalFees: number;
  collectedFees: number;
  pendingFees: number;
  collectionRate: number;
}

interface PendingFeeBooking {
  _id: string;
  bookingId?: number;
  totalAmount: number;
  feeCollection: {
    platformFee: number;
    gst: number;
    collected: boolean;
  };
  providerId: {
    _id: string;
    name: string;
    email: string;
  };
  customerId: {
    name: string;
    email: string;
  };
  bookingDate: string;
  createdAt: string;
}

interface AnalyticsData {
  overview: {
    totalBookings: number;
    completedBookings: number;
    totalCustomerRevenue: number;
  };
  fees: {
    platformFee: FeeData;
    gst: FeeData;
    combined: {
      total: number;
      collected: number;
      pending: number;
    };
  };
  providerBreakdown: ProviderBreakdown[];
  pendingFeeBookings: PendingFeeBooking[];
}

const AdminAnalyticsPage: React.FC = () => {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedProvider, setSelectedProvider] = useState('');
  const [updatingFees, setUpdatingFees] = useState<string | null>(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (selectedProvider) params.append('providerId', selectedProvider);

      const response = await axios.get(
        `${API_BASE_URL}/api/admin/analytics/advanced?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setData(response.data.data);
    } catch (err: any) {
      console.error('Error fetching analytics:', err);
      setError(err.response?.data?.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkCollected = async (bookingId: string) => {
    try {
      setUpdatingFees(bookingId);
      const token = localStorage.getItem('token');
      
      await axios.patch(
        `${API_BASE_URL}/api/admin/analytics/bookings/${bookingId}/fees/collected`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Refresh data
      await fetchAnalytics();
    } catch (err: any) {
      console.error('Error marking fees as collected:', err);
      alert(err.response?.data?.message || 'Failed to mark fees as collected');
    } finally {
      setUpdatingFees(null);
    }
  };

  const handleMarkUncollected = async (bookingId: string) => {
    try {
      setUpdatingFees(bookingId);
      const token = localStorage.getItem('token');
      
      await axios.patch(
        `${API_BASE_URL}/api/admin/analytics/bookings/${bookingId}/fees/uncollected`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Refresh data
      await fetchAnalytics();
    } catch (err: any) {
      console.error('Error marking fees as uncollected:', err);
      alert(err.response?.data?.message || 'Failed to mark fees as uncollected');
    } finally {
      setUpdatingFees(null);
    }
  };

  const handleApplyFilters = () => {
    fetchAnalytics();
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setSelectedProvider('');
    setTimeout(() => fetchAnalytics(), 100);
  };

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white">Revenue & Fee Analytics</h1>
          <p className="text-gray-300 mt-2">Track platform fees, GST collection, and provider performance</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center">
              <Calendar className="h-5 w-5 text-gray-500 mr-2" />
              <span className="text-sm font-medium text-gray-700">Filters:</span>
            </div>

            <div className="flex flex-col">
              <label className="text-xs text-gray-600 mb-1">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-xs text-gray-600 mb-1">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 text-sm"
              />
            </div>

            <button
              onClick={handleApplyFilters}
              disabled={loading}
              className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium disabled:opacity-50"
            >
              Apply Filters
            </button>

            {(startDate || endDate || selectedProvider) && (
              <button
                onClick={handleClearFilters}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm font-medium"
              >
                Clear Filters
              </button>
            )}

            <button
              onClick={fetchAnalytics}
              disabled={loading}
              className="ml-auto px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors text-sm font-medium flex items-center disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {data && (
          <>
            {/* Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-gray-600">Total Bookings</h3>
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">{data.overview.totalBookings}</p>
                <p className="text-sm text-gray-500 mt-2">{data.overview.completedBookings} completed</p>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-gray-600">Customer Revenue</h3>
                  <Banknote className="h-5 w-5 text-green-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">₹{data.overview.totalCustomerRevenue.toFixed(2)}</p>
                <p className="text-sm text-gray-500 mt-2">Total from customers</p>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium text-gray-600">Total Fees Due</h3>
                  <TrendingUp className="h-5 w-5 text-purple-600" />
                </div>
                <p className="text-3xl font-bold text-gray-900">₹{data.fees.combined.total.toFixed(2)}</p>
                <p className="text-sm text-gray-500 mt-2">Platform Fee + GST</p>
              </div>
            </div>

            {/* Fee Collection Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* Platform Fee */}
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="bg-black px-6 py-4">
                  <h3 className="text-lg font-bold text-white">Platform Fee (10%)</h3>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Total</span>
                      <span className="text-2xl font-bold text-gray-900">₹{data.fees.platformFee.total.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center text-green-600">
                      <span className="flex items-center">
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Collected
                      </span>
                      <span className="text-xl font-bold">₹{data.fees.platformFee.collected.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center text-orange-600">
                      <span className="flex items-center">
                        <Clock className="h-4 w-4 mr-2" />
                        Pending
                      </span>
                      <span className="text-xl font-bold">₹{data.fees.platformFee.pending.toFixed(2)}</span>
                    </div>
                    <div className="pt-4 border-t">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Collection Rate</span>
                        <span className="text-lg font-bold text-black">{data.fees.platformFee.collectionRate}%</span>
                      </div>
                      <div className="mt-2 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-black h-2 rounded-full transition-all"
                          style={{ width: `${data.fees.platformFee.collectionRate}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* GST */}
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-green-600 to-teal-600 px-6 py-4">
                  <h3 className="text-lg font-bold text-white">GST (18% on Platform Fee)</h3>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Total</span>
                      <span className="text-2xl font-bold text-gray-900">₹{data.fees.gst.total.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center text-green-600">
                      <span className="flex items-center">
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Collected
                      </span>
                      <span className="text-xl font-bold">₹{data.fees.gst.collected.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center text-orange-600">
                      <span className="flex items-center">
                        <Clock className="h-4 w-4 mr-2" />
                        Pending
                      </span>
                      <span className="text-xl font-bold">₹{data.fees.gst.pending.toFixed(2)}</span>
                    </div>
                    <div className="pt-4 border-t">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Collection Rate</span>
                        <span className="text-lg font-bold text-green-600">{data.fees.gst.collectionRate}%</span>
                      </div>
                      <div className="mt-2 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-green-600 h-2 rounded-full transition-all"
                          style={{ width: `${data.fees.gst.collectionRate}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Provider Breakdown */}
            {data.providerBreakdown.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm mb-8">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900">Per-Provider Breakdown</h3>
                  <p className="text-sm text-gray-600 mt-1">Revenue and fee collection by provider</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Bookings</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Platform Fee</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">GST</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Fees</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Collected</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Pending</th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Rate</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {data.providerBreakdown.map((provider) => (
                        <tr key={provider.providerId} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-3">
                              {provider.providerAvatar ? (
                                <img
                                  src={provider.providerAvatar}
                                  alt={provider.providerName}
                                  className="h-10 w-10 rounded-full object-cover border-2 border-gray-200 flex-shrink-0"
                                />
                              ) : (
                                <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center border-2 border-gray-200 flex-shrink-0">
                                  <span className="text-black font-semibold text-sm">
                                    {provider.providerName.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                              )}
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-900 truncate">{provider.providerName}</p>
                                <p className="text-xs text-gray-500 truncate">{provider.providerEmail}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-center text-sm text-gray-900">{provider.completedBookings}</td>
                          <td className="px-6 py-4 text-right text-sm font-medium text-gray-900">₹{provider.totalRevenue.toFixed(2)}</td>
                          <td className="px-6 py-4 text-right text-sm text-gray-600">₹{provider.platformFee.toFixed(2)}</td>
                          <td className="px-6 py-4 text-right text-sm text-gray-600">₹{provider.gst.toFixed(2)}</td>
                          <td className="px-6 py-4 text-right text-sm font-medium text-black">₹{provider.totalFees.toFixed(2)}</td>
                          <td className="px-6 py-4 text-right text-sm text-green-600">₹{provider.collectedFees.toFixed(2)}</td>
                          <td className="px-6 py-4 text-right text-sm text-orange-600">₹{provider.pendingFees.toFixed(2)}</td>
                          <td className="px-6 py-4 text-center">
                            <span className={`px-2 py-1 text-xs font-bold rounded-full ${
                              provider.collectionRate >= 80 ? 'bg-green-100 text-green-800' :
                              provider.collectionRate >= 50 ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {provider.collectionRate.toFixed(1)}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Pending Fee Bookings */}
            {data.pendingFeeBookings.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center">
                    <AlertCircle className="h-5 w-5 text-orange-600 mr-2" />
                    Pending Fee Collections ({data.pendingFeeBookings.length})
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">Bookings with uncollected platform fees and GST</p>
                </div>
                <div className="divide-y divide-gray-200">
                  {data.pendingFeeBookings.map((booking) => (
                    <div key={booking._id} className="px-6 py-4 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-4 mb-2">
                            <span className="text-sm font-bold text-gray-900">
                              Booking #{booking.bookingId || booking._id.slice(-8).toUpperCase()}
                            </span>
                            <span className="text-sm text-gray-600">
                              {new Date(booking.bookingDate).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">Provider:</span>
                              <p className="font-medium text-gray-900">{booking.providerId.name}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Customer:</span>
                              <p className="font-medium text-gray-900">{booking.customerId.name}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Platform Fee:</span>
                              <p className="font-bold text-black">₹{booking.feeCollection.platformFee.toFixed(2)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">GST:</span>
                              <p className="font-bold text-green-600">₹{booking.feeCollection.gst.toFixed(2)}</p>
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => handleMarkCollected(booking._id)}
                          disabled={updatingFees === booking._id}
                          className="ml-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50 flex items-center"
                        >
                          {updatingFees === booking._id ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Updating...
                            </>
                          ) : (
                            <>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Mark Collected
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.pendingFeeBookings.length === 0 && (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">All Fees Collected!</h3>
                <p className="text-gray-600">There are no pending fee collections at this time.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminAnalyticsPage;

